import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { MinusIcon, PlusIcon } from '@heroicons/react/20/solid';
import { ArrowLongLeftIcon, EyeIcon } from '@heroicons/react/24/outline';
import cn from 'classnames';

import { getServicesNames } from 'bl/consultationForms/getServicesNames.bl';
import { resolveConsultationFormRules } from 'bl/consultationForms/resolveConsultationFormRules.util';
import { resolveReviewRows } from 'bl/consultationForms/resolveReviewRows.util';
import Button from 'components/form/Button.component';
import LINK from 'constants/link.const';
import STRING from 'constants/string.const';
import useIsMobileViewport from 'hooks/useIsMobileViewport.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectSalonServicesCategories } from 'selectors/salon.selectors';
import linkTo from 'utils/links/linkTo.util';

import { feActivateConsultationForm, feSetConsultationFormDraft } from '../consultationForms.action';
import { selectConsultationFormById } from '../consultationForms.selectors';

import CN from './ReviewPage.module.scss';

export const ReviewPage = () => {
  const { t } = useTranslation();
  const { formId } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const salonId = useCurrentSalonId();
  const { isDarkMode } = useTheme();
  const isMobile = useIsMobileViewport();

  const form = useSelector((state) => selectConsultationFormById(state, formId));
  const categories = useSelector(selectSalonServicesCategories);

  const [showServices, setShowServices] = useState(false);

  const builderBasePath = linkTo({ pattern: LINK.settingsFormBuilder, params: { salonId, formId } });
  const formsListPath = linkTo({ pattern: LINK.settingsForms, params: { salonId } });

  const rules = resolveConsultationFormRules(form?.rules);
  const rows = resolveReviewRows({ form, rules, t });
  const serviceNames = useMemo(() => getServicesNames(categories, rules.service_ids), [categories, rules.service_ids]);

  const handleBackToRules = () => history.push(`${builderBasePath}?step=2`);

  const handleSaveAsDraft = () => {
    dispatch(feSetConsultationFormDraft(formId));
    history.push(formsListPath);
  };

  const handleActivate = () => {
    dispatch(feActivateConsultationForm(formId));
    history.push(formsListPath);
  };

  return (
    <div data-bem="ReviewPage" className={cn(CN.component, { [CN.darkMode]: isDarkMode })}>
      <div className={CN.header}>
        <h2 className={CN.title}>{t('consultationForms.review.pageTitle')}</h2>
        <p className={CN.subtitle}>{t('consultationForms.review.pageSubtitle')}</p>
      </div>

      <div className={CN.card}>
        {rows.map((row, index) => (
          <div key={row.key}>
            <div className={CN.row}>
              <span className={CN.label}>{row.label}</span>
              <div className={CN.valueCell}>
                <span className={CN.value}>{row.value}</span>
                {row.showServicesToggle && (
                  <div className={cn(CN.serviceListWrapper, { [CN.serviceListWrapperExpanded]: showServices })} aria-hidden={!showServices}>
                    <div className={CN.serviceListInner}>
                      <ul className={CN.serviceList}>
                        {serviceNames.map((name) => (
                          <li key={name} className={CN.serviceItem}>
                            {STRING.dot} {name}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
              {row.showServicesToggle && (
                <Button
                  variant="empty"
                  className={CN.toggleButton}
                  aria-expanded={showServices}
                  onClick={() => setShowServices((prev) => !prev)}
                  data-testid="ReviewPage__toggleServices"
                  noMargin
                  noVerticalMargin
                  noPadding
                >
                  {showServices ? <MinusIcon className={CN.toggleIcon} /> : <PlusIcon className={CN.toggleIcon} />}
                </Button>
              )}
              {row.key === 'formName' && !isMobile && (
                <Button variant="link" tag="ReviewPage__preview" noMargin noVerticalMargin noPadding className={CN.previewLink} data-testid="ReviewPage__previewDesktop">
                  <EyeIcon className={CN.previewIcon} />
                  {t('consultationForms.review.preview')}
                </Button>
              )}
            </div>
            {index < rows.length - 1 && <div className={CN.divider} />}
          </div>
        ))}
      </div>

      {isMobile && (
        <div className={CN.previewLinkMobile}>
          <Button variant="link" tag="ReviewPage__preview" noMargin noVerticalMargin noPadding className={CN.previewLink} data-testid="ReviewPage__previewMobile">
            <EyeIcon className={CN.previewIcon} />
            {t('consultationForms.review.preview')}
          </Button>
        </div>
      )}

      <div className={CN.footer}>
        {!isMobile && (
          <Button variant="empty" tag="ReviewPage__back" className={CN.backButton} onClick={handleBackToRules} data-testid="ReviewPage__backDesktop" noMargin noVerticalMargin>
            <ArrowLongLeftIcon className={CN.backIcon} />
            <span className={CN.backButtonText}>{t('consultationForms.review.backToRules')}</span>
          </Button>
        )}
        <div className={CN.footerRight}>
          <Button variant="outlineForce" onClick={handleSaveAsDraft} tag="ReviewPage__saveDraft" data-testid="ReviewPage__saveDraft" className={CN.saveDraftButton} xSpace noMargin noVerticalMargin>
            {t('consultationForms.review.saveDraft')}
          </Button>
          <Button variant="primaryForce" onClick={handleActivate} tag="ReviewPage__activate" data-testid="ReviewPage__activate" className={CN.activateButton} xSpace noMargin noVerticalMargin>
            {t('consultationForms.review.activateForm')}
          </Button>
        </div>
      </div>
    </div>
  );
};
